import React, { useCallback, useEffect, useRef } from 'react';
import { MathUtils } from 'three';
import { useDetailViewContext } from '../context/DetailViewContext';
import { useNavigationContext } from '../context/NavigationContext';

interface Props
{
    children?: React.ReactNode,
    scrollSpeedMultiplier?: number,
    touchMultiplier?: number
}

function getClosestPoint(snapPoints: number[], point: number, scrollSpeed: number)
{
  let closestDistance = Infinity;
  let closestPoint = scrollSpeed > 0 ? 1 : 0;

  point += scrollSpeed * 0.01;
  point = MathUtils.clamp(point, 0, 1);

  snapPoints.forEach(p =>
  {
    let distance = p - point;

    if (Math.sign(distance) !== Math.sign(scrollSpeed))
      distance = Infinity;
    else
      distance = Math.abs(distance);

    if (distance < closestDistance) {
      closestDistance = distance;
      closestPoint = p;
    }
  });

  // if we send 1, it hides the latest section and we see white screen.
  return MathUtils.clamp(closestPoint, 0, 0.99);
}

// speeds are units per second
// use these as props so they can be changed where they're used
export default function ScrollWheelController({
  children,
  scrollSpeedMultiplier = 1,
  touchMultiplier = 20
}: Props): JSX.Element
{
  const { setScrollValue, scrollValue, scrollEnabledUp, scrollEnabledDown, sections, scrollUpdate } = useNavigationContext();
  const { detailViewVisible } = useDetailViewContext();
  const scrollSpeed = useRef(0);
  const initScrollValue = useRef(0);  // just to have a copy of scrollvalue on the start of ref that doesn't rerender
  const targetScrollValue = useRef(0);
  const lastTickTime = useRef(0);
  const animationFrameId = useRef(0);
  const lastTouchPos = useRef(0);
  const snapPoints = useRef<number[]>([]);

  useEffect(() =>
  {
    snapPoints.current.length = 0;

    sections.forEach(s =>
    {
      s.snapPoints.forEach(sp =>
      {
        snapPoints.current.push(s.start + sp * (s.end - s.start));
      });
    });

    snapPoints.current.sort((a, b) => a - b);
  }, [ sections ]);

  const onTick = useCallback((currentTime) =>
  {
    if (lastTickTime.current === 0)
      lastTickTime.current = currentTime;
    const deltaTime = Math.min((currentTime - lastTickTime.current) / 1000, 0.05);
    lastTickTime.current = currentTime;

    setScrollValue(scrollValue =>
    {
      initScrollValue.current = scrollValue;

      if (Math.abs(scrollValue - targetScrollValue.current) < 0.001) {
        lastTickTime.current = 0;
        scrollSpeed.current = 0;
        animationFrameId.current = 0;
        return targetScrollValue.current;
      }

      return MathUtils.lerp(scrollValue, targetScrollValue.current, 2 * deltaTime);
    });

    animationFrameId.current = requestAnimationFrame(onTick);
  }, [setScrollValue]);

  useEffect(() => {
    // just using this to make sure it stays in the deps
    if (scrollUpdate !== 0) {
      initScrollValue.current = scrollValue;
      targetScrollValue.current = scrollValue;
      scrollSpeed.current = 0;
    }
    // Should not monitor for scrollValue
    // eslint-disable-next-line
  }, [scrollUpdate]);

  const OnScroll = useCallback((delta) =>
  {
    if(detailViewVisible)
      return;
    if (!scrollEnabledUp && delta < 0)
      return;
    if (!scrollEnabledDown && delta > 0)
      return;
    if(delta === 0)
      return;
    
    scrollSpeed.current = scrollSpeedMultiplier * delta / 10000;

    targetScrollValue.current = getClosestPoint(snapPoints.current, initScrollValue.current, scrollSpeed.current);

    // for some reason, scrolling up needs an extra margin
    if (scrollSpeed.current < 0) {
      targetScrollValue.current = Math.max(targetScrollValue.current - 0.001, 0.0);
    }

    if (animationFrameId.current === 0)
      onTick(0);
  }, [ onTick, scrollSpeedMultiplier, scrollEnabledUp, scrollEnabledDown, detailViewVisible ]);

  const onWheel = useCallback((e: WheelEvent) => OnScroll(e.deltaY), [ OnScroll ]);

  const onTouchMove = useCallback((e: TouchEvent) =>
  {
    if(e.targetTouches.length > 1)
      return;
    if (lastTouchPos.current === 0)
      lastTouchPos.current = e.targetTouches[0].screenY;
    const deltaPos = lastTouchPos.current - e.targetTouches[0].screenY;
    lastTouchPos.current = e.targetTouches[0].screenY;
    OnScroll(deltaPos * touchMultiplier);
  }, [ OnScroll, touchMultiplier ]);

  useEffect(() =>
  {
    document.addEventListener('wheel', onWheel);
    document.addEventListener('touchmove', onTouchMove);
    return function cleanup()
    {
      document.removeEventListener('wheel', onWheel);
      document.removeEventListener('touchmove', onTouchMove);
    };
  }, [ onWheel, onTouchMove ]);

  return <>{children}</>;
}
